import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FiDownload } from 'react-icons/fi'
import { getSessionAuth } from '../utils/sessionAuth'
import { ensureSessionUserId, getSessionUserId } from '../utils/sessionUser'
import { getBrowserTimeZone, getTimeZoneOptions } from '../utils/timezone'
import { getUser, updateUser, updatePin } from '../api/user'
import ContentHeader from '../components/ContentHeader'
import LoadingPlaceholder from '../components/LoadingPlaceholder'
import Loader from '../components/Loading'
import PhoneVerificationPopup from '../components/PhoneVerificationPopup'
import ConnectedCalendars from '../components/ConnectedCalendars'
import '../style/views/SettingsView.css'

type ProfileState = {
  firstName: string
  lastName: string
  email: string
  id: string
  cell: string
}

const assistantContact = {
  firstName: 'Bev',
  lastName: '',
  phoneNumber: '(877) 378-0982',
  company: 'Scribbl',
}

const normalizePhone = (value: string) => {
  const trimmed = value.trim()
  if (!trimmed) return ''
  if (trimmed.startsWith('+')) return trimmed
  const digits = trimmed.replace(/[^0-9]/g, '')
  if (!digits) return ''
  if (digits.length === 10) return `+1${digits}`
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`
  return `+${digits}`
}

const handleDownloadAssistantVcard = () => {
  const digits = assistantContact.phoneNumber.replace(/[^0-9]/g, '')
  const normalizedPhone = digits.length === 10 ? `+1${digits}` : digits ? `+${digits}` : ''
  const fullName = `${assistantContact.firstName} ${assistantContact.lastName}`.trim()
  const vcard = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `N:${assistantContact.lastName};${assistantContact.firstName};;;`,
    `FN:${fullName}`,
    assistantContact.company ? `ORG:${assistantContact.company}` : '',
    normalizedPhone ? `TEL;TYPE=CELL:${normalizedPhone}` : '',
    'END:VCARD',
  ]
    .filter(Boolean)
    .join('\n')
  const blob = new Blob([vcard], { type: 'text/vcard;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `${assistantContact.firstName}-${assistantContact.lastName}.vcf`
    .replace(/\s+/g, '-')
    .toLowerCase()
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

function SettingsView() {
  const COLOR_MODE_STORAGE_KEY = 'calendarColorModeEnabled'
  const DARK_MODE_STORAGE_KEY = 'appDarkModeEnabled'
  const SHOW_COMMANDS_STORAGE_KEY = 'searchShowCommandsByDefault'
  const SHOW_CHATS_STORAGE_KEY = 'myscribbl_show_chats_in_notes'

  const navigate = useNavigate()
  const [isEditing, setIsEditing] = useState(false)
  const [profile, setProfile] = useState<ProfileState>({
    firstName: '',
    lastName: '',
    email: '',
    id: '',
    cell: '',
  })
  const [originalProfile, setOriginalProfile] = useState<ProfileState>({
    firstName: '',
    lastName: '',
    email: '',
    id: '',
    cell: '',
  })
  const [timezone, setTimezone] = useState<string>(getBrowserTimeZone())
  const [originalTimezone, setOriginalTimezone] = useState<string>(getBrowserTimeZone())
  const [pin, setPin] = useState(['', '', '', ''])
  const [pinStatus, setPinStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(
    null,
  )
  const [pinVerifyOpen, setPinVerifyOpen] = useState(false)
  const [pendingPin, setPendingPin] = useState<string | null>(null)
  const [pinVerifyCell, setPinVerifyCell] = useState('')
  const [loadingUser, setLoadingUser] = useState(true)
  const [savingUser, setSavingUser] = useState(false)
  const [updatingPin, setUpdatingPin] = useState(false)
  const [darkModeOn, setDarkModeOn] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.localStorage.getItem(DARK_MODE_STORAGE_KEY) === 'true'
  })
  const [colorModeOn, setColorModeOn] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.localStorage.getItem(COLOR_MODE_STORAGE_KEY) === 'true'
  })
  const [showCommandsOnOpen, setShowCommandsOnOpen] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.localStorage.getItem(SHOW_COMMANDS_STORAGE_KEY) === 'true'
  })
  const [showChatsInNotes, setShowChatsInNotes] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.localStorage.getItem(SHOW_CHATS_STORAGE_KEY) === 'true'
  })

  const pinRefs = useRef<Array<HTMLInputElement | null>>([])
  const [userId, setUserId] = useState<string | null>(() => getSessionUserId())
  const timeZoneOptions = useMemo(getTimeZoneOptions, [])

  useEffect(() => {
    if (typeof window === 'undefined') return
    window.localStorage.setItem(COLOR_MODE_STORAGE_KEY, colorModeOn ? 'true' : 'false')
  }, [colorModeOn])

  useEffect(() => {
    if (typeof window === 'undefined') return
    window.localStorage.setItem(SHOW_COMMANDS_STORAGE_KEY, showCommandsOnOpen ? 'true' : 'false')
  }, [showCommandsOnOpen])

  useEffect(() => {
    if (typeof window === 'undefined') return
    window.localStorage.setItem(SHOW_CHATS_STORAGE_KEY, showChatsInNotes ? 'true' : 'false')
  }, [showChatsInNotes])

  useEffect(() => {
    if (typeof window === 'undefined') return
    window.localStorage.setItem(DARK_MODE_STORAGE_KEY, darkModeOn ? 'true' : 'false')
    if (darkModeOn) {
      document.documentElement.setAttribute('data-theme', 'dark')
    } else {
      document.documentElement.removeAttribute('data-theme')
    }
  }, [darkModeOn])

  const toggleEdit = () => setIsEditing((prev) => !prev)
  const cancelPinChange = () => {
    setPin(['', '', '', ''])
    setPinStatus(null)
  }

  const handlePinVerifyClose = () => {
    setPinVerifyOpen(false)
    setPendingPin(null)
    setPinVerifyCell('')
  }

  const handleViewArchives = () => {
    navigate('/archives')
  }

  const handleViewMemories = () => {
    navigate('/memories')
  }


  const handleCancelEdit = () => {
    setProfile(originalProfile)
    setTimezone(originalTimezone)
    setIsEditing(false)
    cancelPinChange()
  }

  const handlePinDigitChange = (index: number, value: string) => {
    const digit = value.slice(-1).replace(/[^0-9]/g, '')
    const next = [...pin]
    next[index] = digit
    setPin(next)

    if (digit && index < pin.length - 1) {
      pinRefs.current[index + 1]?.focus()
    }
  }

  const handlePinKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace') {
      event.preventDefault()
      if (pin[index]) {
        const next = [...pin]
        next[index] = ''
        setPin(next)
      } else if (index > 0) {
        const next = [...pin]
        next[index - 1] = ''
        setPin(next)
        pinRefs.current[index - 1]?.focus()
      }
    }
  }

  const handleChangePin = () => {
    setPinStatus(null)
    const newPin = pin.join('').trim()
    if (!/^[0-9]{4}$/.test(newPin)) {
      setPinStatus({ type: 'error', message: 'Enter a 4-digit PIN before updating.' })
      return
    }
    const normalizedCell = normalizePhone(profile.cell)
    if (!normalizedCell) {
      setPinStatus({ type: 'error', message: 'Phone number missing. Please contact support.' })
      return
    }
    setPendingPin(newPin)
    setPinVerifyCell(normalizedCell)
    setPinVerifyOpen(true)
  }

  const handlePinVerified = async () => {
    if (!pendingPin) {
      throw new Error('Missing PIN. Please try again.')
    }
    const ensuredId = userId || (await ensureSessionUserId(getSessionAuth()))
    if (!ensuredId) {
      throw new Error('User ID unavailable. Please try again.')
    }
    if (!userId) setUserId(ensuredId)

    try {
      setUpdatingPin(true)
      const text = await updatePin(ensuredId, pendingPin)
      if (text.includes('pin changed successfully')) {
        setPinStatus({ type: 'success', message: 'PIN changed successfully.' })
        setPin(['', '', '', ''])
      } else {
        throw new Error(text || 'Unable to update PIN.')
      }
    } catch (err) {
      console.error('Failed to update PIN', err)
      const message = err instanceof Error ? err.message : 'Failed to update PIN. Please try again.'
      setPinStatus({ type: 'error', message })
      throw err
    } finally {
      setUpdatingPin(false)
    }
  }

  const formFields = useMemo(
    () => (
      <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <label>
          <span>First name</span>
          <input
            className={isEditing ? 'edit-input' : ''}
            value={profile.firstName}
            onChange={(e) => setProfile({ ...profile, firstName: e.target.value })}
            readOnly={!isEditing}
          />
        </label>
        <label>
          <span>Last name</span>
          <input
            className={isEditing ? 'edit-input' : ''}
            value={profile.lastName}
            onChange={(e) => setProfile({ ...profile, lastName: e.target.value })}
            readOnly={!isEditing}
          />
        </label>
        <label>
          <span>Email</span>
          <input
            className="read-only-input"
            type="email"
            value={profile.email}
            readOnly
          />
        </label>
        <label>
          <span>Phone number</span>
          <input
            className={isEditing ? 'edit-input' : ''}
            type="tel"
            value={profile.cell}
            onChange={(e) => setProfile({ ...profile, cell: e.target.value })}
            readOnly={!isEditing}
          />
        </label>
        <label className="full-row">
          <span>Timezone</span>
          {isEditing ? (
            <select
              className="edit-input"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
            >
              {timeZoneOptions.map((zone) => (
                <option key={zone} value={zone}>
                  {zone}
                </option>
              ))}
            </select>
          ) : (
            <input className="edit-input" value={timezone} readOnly />
          )}
        </label>
      </div>
    ),
    [isEditing, profile, timeZoneOptions, timezone],
  )

  useEffect(() => {
    const controller = new AbortController()
    const loadUser = async () => {
      try {
        setLoadingUser(true)
        const ensuredId = userId || (await ensureSessionUserId(getSessionAuth()))
        if (!ensuredId) {
          setLoadingUser(false)
          return
        }
        if (!userId) setUserId(ensuredId)
        const data = await getUser(ensuredId, controller.signal)
        if (data) {
          setProfile({
            id: data.id || ensuredId,
            firstName: data.first_name || '',
            lastName: data.last_name || '',
            email: data.email || '',
            cell: data.cell || '',
          })
          setOriginalProfile({
            id: data.id || ensuredId,
            firstName: data.first_name || '',
            lastName: data.last_name || '',
            email: data.email || '',
            cell: data.cell || '',
          })
          const browserTz = getBrowserTimeZone()
          const storedTz = (data.timezone as string) || ''
          if (storedTz) {
            setTimezone(storedTz)
            setOriginalTimezone(storedTz)
          } else {
            // No timezone persisted for this user. Previously we fell back to
            // the browser timezone for *display only*, which masked the empty
            // stored value: the user saw a correct-looking tz but the backend
            // had none, so the assistant rendered all calendar/times in UTC.
            // Detect the browser tz and persist it immediately so the stored
            // value becomes real and the assistant converts correctly.
            setTimezone(browserTz)
            setOriginalTimezone(browserTz)
            try {
              await updateUser({
                id: data.id || ensuredId,
                email: data.email || '',
                cell: data.cell || '',
                first_name: data.first_name || '',
                last_name: data.last_name || '',
                timezone: browserTz,
              })
            } catch (persistErr) {
              console.error('Failed to persist detected timezone', persistErr)
            }
          }
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          console.error('Failed to load user', err)
        }
      } finally {
        if (!controller.signal.aborted) setLoadingUser(false)
      }
    }
    loadUser()
    return () => controller.abort()
  }, [userId])

  const saveProfileUpdate = async (update: { profile: ProfileState; timezone: string }) => {
    await updateUser({
      id: update.profile.id,
      email: update.profile.email,
      cell: normalizePhone(update.profile.cell),
      first_name: update.profile.firstName,
      last_name: update.profile.lastName,
      timezone: update.timezone,
    })
    setProfile(update.profile)
    setTimezone(update.timezone)
    setOriginalProfile(update.profile)
    setOriginalTimezone(update.timezone)
    setIsEditing(false)
  }

  const handleSave = async () => {
    setSavingUser(true)
    try {
      const snapshot = { profile: { ...profile }, timezone }
      await saveProfileUpdate(snapshot)
    } catch (err) {
      console.error('Failed to save user', err)
    } finally {
      setSavingUser(false)
    }
  }

  if (loadingUser) {
    return <LoadingPlaceholder variant="today" />
  }

  return (
    <>
      <ContentHeader title="Settings"/>
      <div className="settings-layout">
        <div className="settings-grid">
          <div className="card">
            <div className="card-header">
              <div>
                <p className="eyebrow">Profile</p>
                <h2>Your Information</h2>
              </div>
              {isEditing ? (
                <div className="settings-actions">
                  <button
                    type="button"
                    className="primary-button"
                    onClick={handleSave}
                    disabled={savingUser}
                  >
                    Save
                  </button>
                  <button type="button" className="ghost-button" onClick={handleCancelEdit}>
                    Cancel
                  </button>
                </div>
              ) : (
                <button type="button" className="primary-outline" onClick={toggleEdit}>
                  Edit
                </button>
              )}
            </div>
            <div className="section-shell">
              {savingUser && (
                <div className="overlay-loader">
                  <Loader />
                </div>
              )}
              {isEditing ? (
                formFields
              ) : (
                <div className="info-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                  <div className="info-row">
                    <span>First name</span>
                    <strong>{profile.firstName}</strong>
                  </div>
                  <div className="info-row">
                    <span>Last name</span>
                    <strong>{profile.lastName}</strong>
                  </div>
                  <div className="info-row">
                    <span>Email</span>
                    <strong>{profile.email}</strong>
                  </div>
                  <div className="info-row">
                    <span>Phone number</span>
                    <strong>{profile.cell}</strong>
                  </div>
                  <div className="info-row full-row">
                    <span>Timezone</span>
                    <strong>{timezone.replace(/\//g, ' • ').replace(/[_-]/g, ' ')}</strong>
                  </div>
                </div>
              )}
            </div>
            <div className="pin-header">
              <p className="eyebrow">PIN</p>
              {!isEditing && (
              <p className="muted">
                Your PIN is hidden for security. Click Edit to update it anytime.
              </p>
              )}
            </div>
            <div className="pin-row pin-shell">
                {updatingPin && (
                  <div className="overlay-loader">
                    <Loader />
                  </div>
                )}
                {pin.map((digit, pos) => (
                  <input
                    key={`setting-${pos}`}
                    className="pin-input edit-input"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handlePinDigitChange(pos, e.target.value)}
                    onKeyDown={(e) => handlePinKeyDown(pos, e)}
                    ref={(el) => {
                      pinRefs.current[pos] = el
                    }}
                  />
                ))}
                {isEditing && (
                  <>
                  <button
                  className="primary-button"
                  onClick={handleChangePin}
                  disabled={updatingPin || pinVerifyOpen}
                >
                  Save new pin
                </button>
                <button type="button" className="ghost-button" onClick={cancelPinChange}>
                  Cancel
                </button>
                  </>
                )}
                {pinStatus && <span className={`pin-status ${pinStatus.type}`}>{pinStatus.message}</span>}
              </div>
          </div>

          <div className="card assistant-card-wide">
            <div className="card-header">
              <div>
                <p className="eyebrow">myScribbl Assistant</p>
                <h2>Phone number</h2>
              </div>
              <button
                type="button"
                className="ghost-button small"
                onClick={handleDownloadAssistantVcard}
                aria-label="Download vCard"
                title="Download vCard"
              >
                <FiDownload aria-hidden size={16} />
              </button>
            </div>
            <div className="assistant-number">
              <span>Beverly's phone number:</span>
              <strong>{assistantContact.phoneNumber}</strong>
            </div>
          </div>

          {/* Connected Calendars */}
          <div className="card">
            <ConnectedCalendars />
          </div>

          <div className="card">
            <div className="card-header">
              <div>
                <p className="eyebrow">Appearance</p>
                <h2>App</h2>
              </div>
            </div>
            <div className="section-shell" style={{ marginBottom: '12px' }}>
              <div className="settings-toggle-row" style={{ marginBottom: '16px' }}>
                <div>
                  <div className="settings-toggle-label">Dark mode</div>
                  <div className="muted">Toggle dark appearance across the app.</div>
                </div>
                <button
                  type="button"
                  className={`settings-switch ${darkModeOn ? 'on' : 'off'}`}
                  onClick={() => setDarkModeOn((prev) => !prev)}
                  aria-pressed={darkModeOn}
                >
                  <span className="settings-switch-thumb" />
                  {!darkModeOn ? <span className="settings-switch-status">Off</span> : null}
                </button>
              </div>

              <div className="settings-toggle-row" style={{ marginBottom: '16px' }}>
                <div>
                  <div className="settings-toggle-label">Show commands by default</div>
                  <div className="muted">Display all available commands when opening the palette.</div>
                </div>
                <button
                  type="button"
                  className={`settings-switch ${showCommandsOnOpen ? 'on' : 'off'}`}
                  onClick={() => setShowCommandsOnOpen((prev) => !prev)}
                  aria-pressed={showCommandsOnOpen}
                >
                  <span className="settings-switch-thumb" />
                  {!showCommandsOnOpen ? <span className="settings-switch-status">Off</span> : null}
                </button>
              </div>

              <div className="settings-toggle-row">
                <div>
                  <div className="settings-toggle-label">Show chats in call notes</div>
                  <div className="muted">Display AI assistant chats alongside your call history.</div>
                </div>
                <button
                  type="button"
                  className={`settings-switch ${showChatsInNotes ? 'on' : 'off'}`}
                  onClick={() => setShowChatsInNotes((prev) => !prev)}
                  aria-pressed={showChatsInNotes}
                >
                  <span className="settings-switch-thumb" />
                  {!showChatsInNotes ? <span className="settings-switch-status">Off</span> : null}
                </button>
              </div>
            </div>
            <div className="card-header">
              <div>
                <h2>Calendar</h2>
              </div>
            </div>
            <div className="section-shell">
              <div className="settings-toggle-row">
                <div>
                  <div className="settings-toggle-label">Color mode</div>
                  <div className="muted">Toggle alternate calendar color styling.</div>
                </div>
                <button
                  className={`settings-switch ${colorModeOn ? 'on' : 'off'}`}
                  onClick={() => setColorModeOn((prev) => !prev)}
                  aria-pressed={colorModeOn}
                >
                  <span className="settings-switch-thumb" />
                  {!colorModeOn ? <span className="settings-switch-status">Off</span> : null}
                </button>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div>
                <p className="eyebrow">Deletion & Archives</p>
                <h2>Archived conversations</h2>
              </div>
            </div>
            <div className="section-shell">
              <p className="muted" style={{ marginBottom: '10px' }}>
                Review conversations you have archived. Unarchive them from the conversation page.
              </p>
              <button type="button" className="primary-outline" onClick={handleViewArchives}>
                See archives
              </button>
            </div>
          </div>

          <div className='card'>
            <div className='card-header'>
              <div>
                <p className='eyebrow'>Agent</p>
                <h2>Core memories</h2>
              </div>
            </div>
            <div className='section-shell'>
              <p className='muted' style={{ marginBottom: '10px' }}>Review & edit core memories that make Bev personal to you.</p>
              <button type="button" className="primary-outline" onClick={handleViewMemories}>
                See memories
              </button>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div>
                <p className="eyebrow">Subscription</p>
                <h2>Manage your plan</h2>
              </div>
            </div>
            <div className="section-shell">
              <p className="muted" style={{ marginBottom: '10px' }}>
                View your billing status, renewal date, and cancel or change your subscription.
              </p>
              <button type="button" className="primary-outline" onClick={() => navigate('/payments')}>
                Manage subscription
              </button>
            </div>
          </div>
        </div>
        <PhoneVerificationPopup
          open={pinVerifyOpen}
          cell={pinVerifyCell}
          onClose={handlePinVerifyClose}
          onVerified={handlePinVerified}
          description="Enter the 4-digit code sent to your phone to change your PIN."
        />
      </div>
    </>
  )
}

export default SettingsView
